<template>
  <div class="geoContainer" :class="lrmClass">
    <form @submit.prevent>
      <!-- Controles de búsqueda: solo en modo edición -->
      <div class="searchControl mb-2" v-if="!soloMapa">
        <div>
          <label for="location">Buscar ubicación:</label>
          <div class="inputSearch">
            <input v-model="location" type="text" id="location" class="form-control" name="location"
                   placeholder="Escribe una dirección">
            <button type="button" @click="searchLocation" class="btn btn-primary">Buscar</button>
          </div>
        </div>
        <div>
          <label for="googleLink">Buscar por enlace de Google</label>
          <div class="inputSearch">
            <input v-model="googleLocation" type="text" id="googleLink" class="form-control" name="googleLink"
                   placeholder="Enlace de Google">
            <button type="button" @click="searchByGoogleMapsLink" class="btn btn-secondary">Buscar</button>
          </div>
        </div>
      </div>

      <!-- v-model del padre (coordenadas cliente "lat, lng") -->
      <input :value="modelValue" type="text" id="coordinates" name="coordinates" readonly hidden>

      <div class="map">
        <div ref="map" class="map-container" @click="handleMapClick"></div>
      </div>

      <!-- Botones visibles en ambos modos -->
      <div class="btnGeoControl">
        <button type="button" class="btn btn-google" @click="redirectToGoogleMaps" :disabled="!googleMapsLink">
          Enlace Google
        </button>
        <button type="button" class="btn btn-dark" @click="redirectToWazeMaps" :disabled="!googleMapsLink">
          Enlace Waze
        </button>
        <button type="button" id="route" class="btn btn-twitter" @click="toggleRouting">
          {{ routingControl ? 'Ocultar Ruta' : 'Mostrar Ruta' }}
        </button>
      </div>
    </form>
  </div>
</template>

<script>
import L from 'leaflet'
import 'leaflet-routing-machine'

export default {
  name: 'GeolocalizacionComponent',
  props: {
    /** v-model: coordenadas del cliente "lat, lng" */
    modelValue: { type: String, default: '' },
    /** true: solo visualiza (mapa interactivo, marcadores fijos) */
    soloMapa: { type: Boolean, default: false },
    /** Zoom inicial al cargar */
    zoomInicial: { type: Number, default: 17 },
    /**
     * Control de instrucciones de la ruta:
     * 'none' = ocultas, 'small' = letra pequeña, 'default' = normal
     */
    instruccionesRuta: {
      type: String,
      default: 'none',
      validator: v => ['none', 'small', 'default'].includes(v)
    }
  },
  emits: ['update:modelValue'],
  data () {
    return {
      map: null,
      routingControl: null,
      resizeObserver: null,

      // UI
      location: '',
      googleLocation: '',

      // Mi ubicación
      lat: 0,
      lng: 0,

      // Marcadores
      markerCliente: null, // <— puede no existir si no hay coords válidas
      markerActual: null,

      imgPath: process.env.VUE_APP_IMAGE_URL || '',
      iconCliente: null,
      iconActual: null
    }
  },
  computed: {
    coordsClienteObj () {
      return this.parseCoordsString(this.modelValue)
    },
    googleMapsLink () {
      if (!this.coordsClienteObj) return ''
      const { latitud, longitud } = this.coordsClienteObj
      return `https://maps.google.com/?q=${latitud},${longitud}`
    },
    wazeMapsLink () {
      if (!this.coordsClienteObj) return ''
      const { latitud, longitud } = this.coordsClienteObj
      return `https://www.waze.com/ul?ll=${latitud}%2C${longitud}&navigate=yes`
    },
    // Clase para controlar estilos de instrucciones LRM vía CSS scoped
    lrmClass () {
      return {
        'lrm-none': this.instruccionesRuta === 'none',
        'lrm-small': this.instruccionesRuta === 'small'
      }
    }
  },
  mounted () {
    this.prepareIcons()
    this.initMap()
    this.getActualLocation(false)

    // Adaptación al contenedor
    this.resizeObserver = new ResizeObserver(() => {
      this.map?.invalidateSize()
    })
    this.resizeObserver.observe(this.$refs.map)
  },
  beforeUnmount () {
    this.resizeObserver?.disconnect()
    this.clearRouting()
    this.map?.remove()
  },
  watch: {
    // Si el padre cambia el v-model:
    // - si hay coords válidas y NO existe el marcador, lo creamos.
    // - si ya existe, lo movemos.
    // - si pasa a inválido, removemos el marcador.
    modelValue (nuevo) {
      const c = this.parseCoordsString(nuevo)
      if (!this.map) return

      if (c && !this.markerCliente) {
        // Crear marcador cliente por primera vez
        this.markerCliente = L.marker([c.latitud, c.longitud], {
          draggable: !this.soloMapa,
          icon: this.iconCliente
        }).addTo(this.map)

        this.markerCliente.on('dragend', (event) => {
          if (this.soloMapa) return
          const { lat, lng } = event.target.getLatLng()
          this.emitCoords(lat, lng)
        })

        this.panToCliente()
        return
      }

      if (c && this.markerCliente) {
        this.markerCliente.setLatLng([c.latitud, c.longitud])
        this.panToCliente()
        return
      }

      // Si no hay coords válidas y existe el marcador, eliminarlo
      if (!c && this.markerCliente) {
        this.map.removeLayer(this.markerCliente)
        this.markerCliente = null
      }
    },
    // Cambia modo: solo controlamos arrastre del marcador cliente (mapa siempre interactivo)
    soloMapa () {
      this.aplicarModoInteraccion()
    },
    // Si cambian instrucciones y hay ruta, el CSS ya hace el trabajo
    instruccionesRuta () {
      // No es necesario tocar DOM aquí, el CSS scoped lo maneja.
    }
  },
  methods: {
    prepareIcons () {
      this.iconCliente = L.icon({
        iconUrl: `${this.imgPath}/axccLocationBlue.svg`,
        iconSize: [30, 40],
        iconAnchor: [15, 30]
      })
      this.iconActual = L.icon({
        iconUrl: `${this.imgPath}/axccLocation.svg`,
        iconSize: [30, 40],
        iconAnchor: [15, 30]
      })
    },

    initMap () {
      const c = this.coordsClienteObj
      const start = c ? [c.latitud, c.longitud] : [this.lat || 14.0818, this.lng || -87.2068]

      if (this.map) this.map.remove()

      this.map = L.map(this.$refs.map, { zoomControl: true }).setView(start, this.zoomInicial)

      L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; OpenStreetMap contributors'
      }).addTo(this.map)

      // Marcador del cliente — SOLO si hay coords válidas
      if (c) {
        this.markerCliente = L.marker(start, {
          draggable: !this.soloMapa,
          icon: this.iconCliente
        }).addTo(this.map)

        this.markerCliente.on('dragend', (event) => {
          if (this.soloMapa) return
          const { lat, lng } = event.target.getLatLng()
          this.emitCoords(lat, lng)
        })
      } else {
        this.markerCliente = null
      }

      // Marcador de mi ubicación (nunca draggable)
      this.markerActual = L.marker([this.lat, this.lng], {
        draggable: false,
        icon: this.iconActual
      }).addTo(this.map)

      // Click en mapa para mover cliente (solo edición)
      this.map.on('click', (e) => this.handleMapClick(e))

      if (c) this.focusClienteInicial()
      this.aplicarModoInteraccion()
    },

    aplicarModoInteraccion () {
      // Mapa siempre interactivo (pan/zoom habilitados)
      // Solo controlamos el arrastre del marcador del cliente si existe
      if (this.markerCliente?.dragging) {
        this.soloMapa ? this.markerCliente.dragging.disable()
                      : this.markerCliente.dragging.enable()
      }
      // Los clics para mover se bloquean en handleMapClick con un guard
    },

    parseCoordsString (str) {
      if (!str || typeof str !== 'string') return null
      const [latStr, lngStr] = str.split(',').map(s => s.trim())
      const latitud = parseFloat(latStr)
      const longitud = parseFloat(lngStr)
      if (Number.isNaN(latitud) || Number.isNaN(longitud)) return null
      return { latitud, longitud }
    },

    emitCoords (lat, lng) {
      this.$emit('update:modelValue', `${lat}, ${lng}`)
      this.panToCliente()
    },

    panToCliente () {
      const c = this.coordsClienteObj
      if (!this.map || !c) return
      const current = this.map.getZoom()
      this.map.panTo([c.latitud, c.longitud])
      this.map.setZoom(current) // preserva zoom
    },

    focusClienteInicial () {
      const c = this.coordsClienteObj
      if (!c) return
      this.map.setView([c.latitud, c.longitud], this.zoomInicial)
    },

    getActualLocation (centerMap = false) {
      if (!navigator.geolocation) return
      navigator.geolocation.getCurrentPosition((pos) => {
        this.lat = pos.coords.latitude
        this.lng = pos.coords.longitude
        this.markerActual?.setLatLng([this.lat, this.lng])
        if (centerMap) {
          const c = this.coordsClienteObj
          if (!c) this.map.setView([this.lat, this.lng], this.zoomInicial)
        }
      })
    },

    handleMapClick (event) {
      if (this.soloMapa) return // en soloMapa no cambia la ubicación del cliente
      if (!event?.latlng) return
      const { lat, lng } = event.latlng

      // Si no existe el marcador del cliente aún, crearlo en el primer click válido
      if (!this.markerCliente) {
        this.markerCliente = L.marker([lat, lng], {
          draggable: true,
          icon: this.iconCliente
        }).addTo(this.map)

        this.markerCliente.on('dragend', (ev) => {
          const { lat: dlat, lng: dlng } = ev.target.getLatLng()
          this.emitCoords(dlat, dlng)
        })
      } else {
        this.markerCliente.setLatLng([lat, lng])
      }

      this.emitCoords(lat, lng)
    },

    async searchLocation () {
      if (!this.location) return alert('Ingresa una dirección para buscar.')
      try {
        const url = `https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(this.location)}&countrycodes=HN`
        const res = await fetch(url)
        const data = await res.json()
        if (!data?.length) return alert('No se encontró la ubicación en Honduras.')
        const lat = parseFloat(data[0].lat)
        const lon = parseFloat(data[0].lon)

        // Crear marcador si no existe aún
        if (!this.markerCliente) {
          this.markerCliente = L.marker([lat, lon], {
            draggable: !this.soloMapa,
            icon: this.iconCliente
          }).addTo(this.map)

          this.markerCliente.on('dragend', (ev) => {
            if (this.soloMapa) return
            const { lat: dlat, lng: dlng } = ev.target.getLatLng()
            this.emitCoords(dlat, dlng)
          })
        } else {
          this.markerCliente.setLatLng([lat, lon])
        }

        this.emitCoords(lat, lon)
      } catch (e) {
        console.error(e)
        alert('Error al buscar la ubicación.')
      }
    },

    searchByGoogleMapsLink () {
      if (!this.googleLocation) return alert('Ingresa un enlace de Google Maps para buscar.')
      const regexAt = /@(-?\d+\.?\d*),(-?\d+\.?\d*),\d+\.?\d*z/
      const match = this.googleLocation.match(/q=([-+]?\d+\.\d+),([-+]?\d+\.\d+)/) || this.googleLocation.match(regexAt)
      if (!match) return alert('El enlace no es válido.')
      const lat = parseFloat(match[1])
      const lon = parseFloat(match[2])

      if (!this.markerCliente) {
        this.markerCliente = L.marker([lat, lon], {
          draggable: !this.soloMapa,
          icon: this.iconCliente
        }).addTo(this.map)

        this.markerCliente.on('dragend', (ev) => {
          if (this.soloMapa) return
          const { lat: dlat, lng: dlng } = ev.target.getLatLng()
          this.emitCoords(dlat, dlng)
        })
      } else {
        this.markerCliente.setLatLng([lat, lon])
      }

      this.emitCoords(lat, lon)
    },

    redirectToGoogleMaps () {
      if (!this.googleMapsLink) return
      window.open(this.googleMapsLink, '_blank')
    },
    redirectToWazeMaps () {
      if (!this.wazeMapsLink) return
      window.open(this.wazeMapsLink, '_blank')
    },

    toggleRouting () {
      // Si ya hay ruta, la quitamos (toggle off)
      if (this.routingControl) {
        this.clearRouting()
        return
      }
      // Crear ruta (toggle on)
      const destino = this.markerCliente?.getLatLng()
      const origen = this.markerActual?.getLatLng()
      if (!destino || !origen || (origen.lat === 0 && origen.lng === 0)) {
        console.log('Ubicación actual no válida o destino inexistente.')
        return
      }
      this.routingControl = L.Routing.control({
        waypoints: [L.latLng(origen), L.latLng(destino)],
        router: L.Routing.osrmv1({ serviceUrl: 'https://router.project-osrm.org/route/v1' }),
        routeWhileDragging: false,
        showAlternatives: true,
        lineOptions: { styles: [{ color: '#8788ff', weight: 5 }] },
        altLineOptions: { styles: [{ color: '#ff0000', weight: 5 }] },
        createMarker: () => null
      }).addTo(this.map)

      // CSS controla visibilidad/ tamaño de instrucciones
    },

    clearRouting () {
      if (this.routingControl) {
        try { this.map.removeControl(this.routingControl) } catch {
          // ya removido
          console.log('El control de ruta ya fue removido.')
        }
        this.routingControl = null
      }
    }
  }
}
</script>

<style scoped>
.form-control { max-width: 200px; height: 35px; }
.geoContainer { width: 100%; height: 100%; }
.geoContainer > form {
  width: 100%; height: 100%;
  display: flex; flex-direction: column;
  justify-content: space-between; align-items: center;
}
.map { flex: 5; width: 100%; height: 100%; }
.map > .map-container { height: 100%; min-height: 320px; }
.btnGeoControl {
  flex: 1; display: flex; justify-content: flex-start; align-items: center;
  margin-top: 10px; width: 100%; gap: 20px;
}
.searchControl { display: flex; flex: 1; justify-content: center; align-items: center; gap: 20px; flex-wrap: wrap; }
.searchControl > div {
  flex: 1; width: 100%; display: flex; justify-content: center; align-items: center; gap: 10px;
}
.inputSearch { width: 100%; height: 35px; display: flex; justify-content: space-between; align-items: center; gap: 6px; }
.inputSearch > input { margin: 0; height: 35px; }
.inputSearch > button { width: 30%; padding: 5px; height: 35px; margin: 0; }

/* Ocultar alternativas siempre */
:deep(.leaflet-routing-alternatives-container) { display: none !important; }

/* Control de instrucciones según prop */
.lrm-none :deep(.leaflet-routing-container) { display: none !important; }
.lrm-small :deep(.leaflet-routing-container) { font-size: 11px !important; }

/* Opcional: compactar un poco el panel cuando es small */
.lrm-small :deep(.leaflet-routing-container .leaflet-routing-collapse-btn) { transform: scale(0.9); }
.lrm-small :deep(.leaflet-routing-container h2) { font-size: 12px; margin: 4px 0; }
.lrm-small :deep(.leaflet-routing-instructions) { line-height: 1.1; }

@media (max-width: 768px) {
  .btnGeoControl { flex-wrap: wrap; gap: 10px; justify-content: center; }
  .searchControl { font-size: .85rem; flex-direction: column; gap: 6px; }
  .inputSearch { height: 32px; }
  .inputSearch > button { width: 40%; height: 32px; }
}
button { margin-top: 10px; }
</style>
